import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { LinkSummary, SiteSummary, TagFilter } from '../../models/cms-models';
import { LinksService } from '../../services/links-service';
import { SiteContextService } from '../../services/site-context.service';

@Component({
  selector: 'app-links-page',
  imports: [CommonModule],
  templateUrl: './links-page.html',
  styleUrl: './links-page.css',
})
export class LinksPage implements OnInit {
  private siteContext = inject(SiteContextService);
  private linksService = inject(LinksService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  sites: SiteSummary[] = [];
  links: LinkSummary[] = [];
  selectedSiteId: string | null = null;
  loading = false;
  deletingLinkId: string | null = null;
  error: string | null = null;
  tagFilter: TagFilter = {
    tags: [],
    tagMode: 'all',
  };
  private loadedSiteId: string | null = null;

  async ngOnInit(): Promise<void> {
    await this.siteContext.ensureLoaded();
    combineLatest([this.siteContext.sites$, this.siteContext.activeSiteId$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([sites, activeSiteId]) => {
        void this.syncSelectedSite(sites, activeSiteId);
      });
  }

  get selectedSiteName(): string {
    return this.sites.find((site) => site.id === this.selectedSiteId)?.name ?? 'site';
  }

  formatRating(rating: number): string {
    return rating === -1 ? 'None' : `${rating}/10`;
  }

  isFilterActive(tag: string): boolean {
    return this.tagFilter.tags.includes(tag);
  }

  createLink(): void {
    if (!this.selectedSiteId || this.deletingLinkId) {
      return;
    }

    void this.router.navigate(['/links/new']);
  }

  openLink(linkId: string): void {
    if (this.deletingLinkId) {
      return;
    }

    void this.router.navigate(['/links', linkId]);
  }

  async deleteLink(linkId: string): Promise<void> {
    if (!this.selectedSiteId || this.deletingLinkId) {
      return;
    }

    const confirmed = window.confirm('Delete this link? This cannot be undone.');
    if (!confirmed) {
      return;
    }

    this.deletingLinkId = linkId;
    this.error = null;

    try {
      await this.linksService.deleteLink(linkId);
      await this.loadLinks(this.selectedSiteId);
    } catch {
      this.error = 'Failed to delete the selected link.';
    } finally {
      this.deletingLinkId = null;
    }
  }

  async toggleTagFilter(tag: string): Promise<void> {
    const tags = this.tagFilter.tags.includes(tag)
      ? this.tagFilter.tags.filter((existingTag) => existingTag !== tag)
      : [...this.tagFilter.tags, tag];

    this.tagFilter = {
      tags,
      tagMode: 'all',
    };

    await this.reloadSelectedSite();
  }

  async clearFilters(): Promise<void> {
    this.tagFilter = {
      tags: [],
      tagMode: 'all',
    };

    await this.reloadSelectedSite();
  }

  private async syncSelectedSite(sites: SiteSummary[], activeSiteId: string | null): Promise<void> {
    this.sites = sites;

    if (!sites.length) {
      this.selectedSiteId = null;
      this.links = [];
      this.loadedSiteId = null;
      return;
    }

    const targetSiteId = this.siteContext.ensureValidActiveSite(activeSiteId);
    if (!targetSiteId) {
      return;
    }

    this.selectedSiteId = targetSiteId;

    if (this.loadedSiteId === targetSiteId) {
      return;
    }

    this.links = [];
    await this.loadLinks(targetSiteId);
  }

  private async loadLinks(siteId: string): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      this.links = await this.linksService.getSiteLinks(siteId, this.tagFilter);
      this.loadedSiteId = siteId;
    } catch {
      this.error = 'Failed to load links for the selected site.';
    } finally {
      this.loading = false;
    }
  }

  private async reloadSelectedSite(): Promise<void> {
    if (!this.selectedSiteId) {
      return;
    }

    await this.loadLinks(this.selectedSiteId);
  }
}
